import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, take } from 'rxjs';
import { TableComponent } from '../../../core/table/table.component';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { BaseSearchOptions } from '../../../models/core/table/BaseSearchOptions';
import { FilterDataType } from '../../../models/core/table/FilterDataType';
import { QueryBuilderParams } from '../../../models/core/table/QueryBuilderParams';
import { TableActionButton } from '../../../models/core/table/TableActionButton';
import { TableColumn } from '../../../models/core/table/TableColumn';
import { TableEvent } from '../../../models/core/table/TableEvent';
import { TableExpandColumn } from '../../../models/core/table/TableExpandColumn';
import { TableSearchEventData } from '../../../models/core/table/TableSearchEventData';
import { Die } from '../../../models/domain/Die';
import { DieListViewDto } from '../../../models/domain/DieListViewDto';
import { EventTypes } from '../../../models/enums/EventTypes';
import { GroupType } from '../../../models/enums/GroupType';
import { ModalText } from '../../../models/enums/ModalText';
import { RoleEnum } from '../../../models/enums/RoleEnum';
import { TableCellTypeEnum } from '../../../models/enums/TableCellTypeEnum';
import { AuthenticationService } from '../../../services/authentication.service';
import { DieService } from '../../../services/die.service';
import { LoadingService } from '../../../services/loading.service';
import { ModalService } from '../../../services/modal.service';
import { RoleService } from '../../../services/roleService';
import { getSearchOperator } from '../../../utilities/common-functions';
import { UrlHelper } from '../../../utilities/UrlHelper';
import { DieDetailsComponent } from '../die-details/die-details.component';


@Component({
    selector: 'app-die-list',
    imports: [RouterLink, TableComponent, DieDetailsComponent, NgIf, NgFor],
    templateUrl: './die-list.component.html',
    styleUrl: './die-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DieListComponent {

    //#region Table Variables
    public expandColumn!: TableExpandColumn;
    public columns!: TableColumn[];
    public actions!: TableActionButton[];
    public totalRecords = 0;
    public pageIndex = 0;
    public pageSize = 10;
    public pageSizeOptions: number[] = [10, 50, 100];
    public searchOptions!: BaseSearchOptions;
    //#endregion

    dieId = 0;
    die!: Die;

    // Popup Trigger
    classApplied = false;
    isToggled = false;
    showModal = false;
    deleteModalData!: Die;
    ConfirmText = ModalText.ConfirmYes
    CancelText = ModalText.ConfirmNo

    // Added new properties for image popup
    showImageModal = false;
    selectedImageUrl = '';
    selectedImageTitle = '';
    //end

    constructor(
        public themeService: CustomizerSettingsService,
        public dieService: DieService,
        private modalService: ModalService,
        private toasterService: ToastrService,
        private roleService: RoleService,
        private loadingService: LoadingService,
        public authService: AuthenticationService

    ) {
        this.initialize();
    }

    ngOnInit(): void {
        this.getData();
    }

    private initialize(): void {
        this.themeService.isToggled$.subscribe(isToggled => {
            this.isToggled = isToggled;
        });

        this.searchOptions = new BaseSearchOptions(undefined, 'no', 0, this.pageIndex, this.pageSize);
        this.buildTable();
    }

    private getData(): void {
        this.loadingService.showLoading();
        this.dieService.get(this.searchOptions).pipe(
            take(1),
            catchError((error) => {
                if (error && error.error) {
                    if (error.error.isCustom) {
                        this.toasterService.warning(error.error.message);
                    } else {
                        this.toasterService.error('Something went wrong!');
                    }
                } else {
                    this.toasterService.error('Something went wrong!');
                }
                return of(null);
            }),
            finalize(() => {
                this.loadingService.hideLoading();
            }),
        ).subscribe(result => {
            if (result) {
                this.totalRecords = result.body?.totalCount ?? 0;
            }
            this.loadingService.hideLoading();
        });
    }

    public buildTable(): void {

        //this.expandColumn = new TableExpandColumn('dieDetails', 'Details', (data: Die) => data.dieDetails, DieDetailsComponent, true, { tooltip: 'Die Details' });
        this.expandColumn = new TableExpandColumn(0, 5);


        this.columns = [
            new TableColumn('no', 'No', FilterDataType.String, true, false, (data: DieListViewDto) => `${data.no}`, true, { tooltip: 'No' }, TableCellTypeEnum.TEXT),
            new TableColumn('name', 'Name', FilterDataType.String, true, false, (data: DieListViewDto) => `${data.name}`, true, { tooltip: 'Name' }, TableCellTypeEnum.TEXT),
            new TableColumn('Category.Name', 'Category Name', FilterDataType.String, true, false, (data: DieListViewDto) => `${data?.categoryName}`, true, { tooltip: 'Category Name', }, TableCellTypeEnum.TEXT),
            //new TableColumn('photoUrl', 'Die Photo', FilterDataType.String, false, false, (data: DieListViewDto) => `${this.getImageUrl(data?.photoUrl)}`, false, { tooltip: 'Die Photo', styleExpression: "padding:5px" }, TableCellTypeEnum.IMAGE),
            new TableColumn('photoUrl', 'Die Photo', FilterDataType.String, false, false,
                (data: DieListViewDto) => `${this.getImageUrl(data?.photoUrl)}`,
                false,
                {
                    tooltip: 'Die Photo',
                    styleExpression: "padding:5px",
                    // Add image click callback
                    onImageClick: (event: MouseEvent, data: DieListViewDto) => this.onImageClick(event, data)
                },
                TableCellTypeEnum.IMAGE
            ),


            new TableColumn('Tenant.Name', 'Tenant Name', FilterDataType.String, true, false, (data: DieListViewDto) => `${data?.tenantName}`, true, { tooltip: 'Tenant Name' }, TableCellTypeEnum.TEXT),
        ];

        this.actions = [];
        if (this.authService.hasRoles([RoleEnum.ClientSuperAdmin])) {
            this.actions.push(
                new TableActionButton("edit", (event, value) => this.onEditClick(event, value), (value) => this.onDisabledClick(), "Edit", "Edit"
                ),
                new TableActionButton("delete", (event, value) => this.onDeleteClick(event, value), (value) => this.onDisabledClick(), "Delete", "Delete"
                )
            );
        }
    }

    public onAddClick(): void {
        //this.die = new Die;
        this.dieId = 0;
        this.toggleClass();
    }

    public onEditClick(event: MouseEvent, data: Die): void {
        event.stopPropagation();
        //this.die = data;
        this.dieId = data.id!;
        this.toggleClass();
    }

    public onDeleteClick(event: MouseEvent, data: Die): void {
        event.stopPropagation();
        this.modalService.openModal(
            'Delete Confirmation',
            `Are you sure you want to delete <strong> ${data.no} </strong> ?`,
            ModalText.ConfirmYes,
            ModalText.ConfirmNo
        );

        this.modalService.onConfirm.subscribe((isConfirm) => {
            if (isConfirm && data.id) {
                this.loadingService.showLoading();
                this.dieService.delete(data.id).pipe(
                    take(1),
                    catchError((error) => {
                        if (error && error.error) {
                            if (error.error.isCustom) {
                                this.toasterService.warning(error.error.message);
                            } else {
                                this.toasterService.error('Something went wrong!');
                            }
                        } else {
                            this.toasterService.error('Something went wrong!');
                        }
                        return of(null);
                    })
                ).subscribe(result => {
                    if (result?.body) {
                        this.toasterService.info("Record delete successfully.");
                        this.getData();
                    }
                    this.loadingService.hideLoading();
                });
            }
        });
    }

    public onDisabledClick(): boolean {
        return false;
    }

    public tableEvent(event: TableEvent<any>): void {
        if (event.type === EventTypes.TABLE) {
            const eventValue = event.data as TableSearchEventData;

            this.searchOptions.updateByObject(eventValue.baseSearchOptions);

            if (eventValue.columnFilterValues) {
                this.createFilter(eventValue);
            }
            else {
                this.searchOptions.filter = '';
            }
            this.getData();
        }
        if (event.type === EventTypes.CLEAR) {
            this.clearFilters();
            this.getData();
        }
        // Handle generic image click event (alternative approach)
        if (event.type === EventTypes.IMAGECLICK) {
            const { row, column } = event.data;
            this.onImageClick(event.data.event, row);
        }
    }

    public clearFilters(): void {
        this.pageIndex = 0;
        this.pageSize = 10;
        this.searchOptions = new BaseSearchOptions(undefined, 'no', 0, this.pageIndex, this.pageSize);
    }

    private createFilter(eventValue: TableSearchEventData): void {
        const queryParams: QueryBuilderParams[] = [];

        for (const [columnName, { dataType, opType, searchValue }] of Object.entries(eventValue.columnFilterValues)) {
            const searchOperator = getSearchOperator(opType.toString());
            if (!searchOperator) continue; // Skip if no valid operator

            queryParams.push({
                fieldName: columnName, // Adjust as necessary for your field mapping
                dataType: dataType,
                searchOperator,
                searchValue: searchValue,
                groupType: GroupType.OR
            });
        }

        this.searchOptions.filter = this.searchOptions.generateQuery(queryParams);
    }

    public toggleClass(): void {
        this.classApplied = !this.classApplied;
    }

    public classAppliedEvent(flag: boolean): void {
        this.classApplied = flag;
    }

    public saveConfirmationEvent(isSaved: boolean): void {
        if (isSaved) {
            this.getData();
        }
    }

    public getImageUrl(path: string): string {
        return UrlHelper.getImageUrl(path);
    }


    // Added new method to handle image clicks
    public onImageClick(event: MouseEvent, data: DieListViewDto): void {
        // Set modal data
        this.selectedImageUrl = UrlHelper.getImageUrl(data?.photoUrl);
        this.selectedImageTitle = `${data.no} - ${data.name} - ${data.categoryName}`;

        // Show modal
        this.showImageModal = true;
    }

    // Add method to close image modal
    public closeImageModal(): void {
        this.showImageModal = false;
        this.selectedImageUrl = '';
        this.selectedImageTitle = '';
    }
}